// [SERVICE] Stripe API Service - Intégration paiements et subscriptions
// [RÔLE] Gestion des appels API Stripe pour checkout, portal, subscription
// [ARCHITECTURE] Communication avec famla-api:9540/api/v1/stripe

import FamlaServices from "./FamlaServices";

/**
 * Service pour gérer l'intégration Stripe (paiements, subscriptions, portal)
 * Communique avec les endpoints /api/v1/stripe/*
 */
class StripeService {
    /**
     * Crée une session Stripe Checkout pour activation plan ou top-up
     * @param plan - Plan à activer ('ESSENTIAL' | 'PLUS') ou null pour top-up uniquement
     * @param companyId - ID de la company
     * @param topupCredits - Nombre de crédits pour top-up (optionnel)
     * @param successUrl - URL de redirection en cas de succès
     * @param cancelUrl - URL de redirection en cas d'annulation
     * @param accessToken - Token JWT d'authentification
     * @returns Session Stripe avec URL de redirection
     */
    static async createCheckoutSession(
        plan: string | null,
        companyId: string,
        topupCredits: number | null,
        successUrl: string,
        cancelUrl: string,
        accessToken: string
    ) {
        try {
            const body: any = {
                company_id: companyId,
                success_url: successUrl,
                cancel_url: cancelUrl
            };
            
            // N'ajouter que les paramètres non-null (évite "Cannot specify both plan and topup_credits")
            if (plan) {
                body.plan = plan;
            }
            if (topupCredits) {
                body.topup_credits = topupCredits;
            }

            const response = await FamlaServices(
                "api_host",
                `/stripe/checkout-session`,
                "POST",
                body, // ✅ Enlever JSON.stringify - FamlaServices s'en occupe
                accessToken
            );

            if (response.status === 200) {
                return {
                    success: true,
                    data: response.body.data,
                };
            } else if (response.status === 401) {
                return {
                    success: false,
                    error: "Unauthorized",
                    message: response.body.description || "Authentication required",
                };
            } else if (response.status === 400) {
                return {
                    success: false,
                    error: "Bad Request",
                    message: response.body.description || "Invalid request parameters",
                };
            } else {
                return {
                    success: false,
                    error: "API Error",
                    message: response.body.description || "Failed to create checkout session",
                };
            }
        } catch (error) {
            console.error("[StripeService] createCheckoutSession error:", error);
            return {
                success: false,
                error: "Network Error",
                message: error instanceof Error ? error.message : "Unknown error",
            };
        }
    }

    /**
     * Crée une session Stripe Customer Portal pour gestion subscription
     * @param companyId - ID de la company
     * @param returnUrl - URL de retour après gestion
     * @param accessToken - Token JWT d'authentification
     * @returns Session portal avec URL de redirection
     */
    static async createPortalSession(
        companyId: string,
        returnUrl: string,
        accessToken: string
    ) {
        try {
            const body = {
                company_id: companyId,
                return_url: returnUrl
            };

            const response = await FamlaServices(
                "api_host",
                `/stripe/portal-session`,
                "POST",
                body, // ✅ Enlever JSON.stringify - FamlaServices s'en occupe
                accessToken
            );

            if (response.status === 200) {
                return {
                    success: true,
                    data: response.body.data,
                };
            } else if (response.status === 401) {
                return {
                    success: false,
                    error: "Unauthorized",
                    message: response.body.description || "Authentication required",
                };
            } else if (response.status === 404) {
                return {
                    success: false,
                    error: "Not Found",
                    message: response.body.description || "No Stripe customer found for this company",
                };
            } else {
                return {
                    success: false,
                    error: "API Error",
                    message: response.body.description || "Failed to create portal session",
                };
            }
        } catch (error) {
            console.error("[StripeService] createPortalSession error:", error);
            return {
                success: false,
                error: "Network Error",
                message: error instanceof Error ? error.message : "Unknown error",
            };
        }
    }

    /**
     * Récupère les détails de la subscription Stripe (plan PLUS)
     * @param companyId - ID de la company
     * @param accessToken - Token JWT d'authentification
     * @returns Détails de la subscription
     */
    static async getSubscriptionDetails(companyId: string, accessToken: string) {
        try {
            const response = await FamlaServices(
                "api_host",
                `/stripe/subscription/${companyId}`,
                "GET",
                "",
                accessToken
            );

            if (response.status === 200) {
                return {
                    success: true,
                    data: response.body.data,
                };
            } else if (response.status === 401) {
                return {
                    success: false,
                    error: "Unauthorized",
                    message: response.body.description || "Authentication required",
                };
            } else if (response.status === 404) {
                return {
                    success: false,
                    error: "Not Found",
                    message: response.body.description || "No active subscription found",
                };
            } else {
                return {
                    success: false,
                    error: "API Error",
                    message: response.body.description || "Failed to fetch subscription details",
                };
            }
        } catch (error) {
            console.error("[StripeService] getSubscriptionDetails error:", error);
            return {
                success: false,
                error: "Network Error",
                message: error instanceof Error ? error.message : "Unknown error",
            };
        }
    }

    /**
     * Génère l'URL de succès pour redirection Stripe
     * @param baseUrl - URL de base de l'application (ex: https://famla.com)
     * @param plan - Plan acheté
     * @param isTopup - Si c'est un top-up
     * @returns URL de succès complète
     */
    static generateSuccessUrl(baseUrl: string, plan?: string, isTopup?: boolean): string {
        const params = new URLSearchParams();
        params.append('payment', 'success');
        if (plan) params.append('plan', plan);
        if (isTopup) params.append('topup', 'true');
        return `${baseUrl}/payment-success?${params.toString()}`;
    }

    /**
     * Génère l'URL d'annulation pour redirection Stripe
     * @param baseUrl - URL de base de l'application
     * @returns URL d'annulation complète
     */
    static generateCancelUrl(baseUrl: string): string {
        return `${baseUrl}/payment-cancel`;
    }

    /**
     * Détermine si une company peut utiliser le Customer Portal
     * @param plan - Plan actuel de la company
     * @param stripeCustomerId - ID customer Stripe (optionnel)
     * @returns true si le portal est disponible
     */
    static canAccessCustomerPortal(plan: string, stripeCustomerId?: string): boolean {
        // Portal disponible pour ESSENTIAL (avec customer ID) et PLUS
        return (plan === 'ESSENTIAL' || plan === 'PLUS') && !!stripeCustomerId;
    }

    /**
     * Récupère les méthodes de paiement enregistrées pour une company
     * @param companyId - ID de la company
     * @param accessToken - Token JWT d'authentification
     * @returns Liste des payment methods avec indication de la méthode par défaut
     */
    static async getPaymentMethods(companyId: string, accessToken: string) {
        try {
            const response = await FamlaServices(
                "api_host",
                `/stripe/payment-methods/${companyId}`,
                "GET",
                "",
                accessToken
            );

            if (response.status === 200) {
                return {
                    success: true,
                    data: response.body.data, // Contient payment_methods[] et default_payment_method
                };
            } else if (response.status === 401) {
                return {
                    success: false,
                    error: "Unauthorized",
                    message: response.body.description || "Authentication required",
                };
            } else if (response.status === 404) {
                return {
                    success: false,
                    error: "Not Found",
                    message: response.body.description || "No Stripe customer found",
                };
            } else {
                return {
                    success: false,
                    error: "API Error",
                    message: response.body.description || "Failed to fetch payment methods",
                };
            }
        } catch (error) {
            console.error("[StripeService] getPaymentMethods error:", error);
            return {
                success: false,
                error: "Network Error",
                message: error instanceof Error ? error.message : "Unknown error",
            };
        }
    }

    /**
     * Récupère les factures Stripe pour une company
     * @param companyId - ID de la company
     * @param limit - Nombre max de factures (défaut 10)
     * @param accessToken - Token JWT d'authentification
     * @returns Liste des factures avec PDFs et hosted URLs
     */
    static async getInvoices(
        companyId: string,
        limit: number = 10,
        accessToken: string
    ) {
        try {
            const response = await FamlaServices(
                "api_host",
                `/stripe/invoices/${companyId}?limit=${limit}`,
                "GET",
                "",
                accessToken
            );

            if (response.status === 200) {
                return {
                    success: true,
                    data: response.body.data, // Contient total et invoices[]
                };
            } else if (response.status === 401) {
                return {
                    success: false,
                    error: "Unauthorized",
                    message: response.body.description || "Authentication required",
                };
            } else if (response.status === 404) {
                return {
                    success: false,
                    error: "Not Found",
                    message: response.body.description || "No Stripe customer found",
                };
            } else {
                return {
                    success: false,
                    error: "API Error",
                    message: response.body.description || "Failed to fetch invoices",
                };
            }
        } catch (error) {
            console.error("[StripeService] getInvoices error:", error);
            return {
                success: false,
                error: "Network Error",
                message: error instanceof Error ? error.message : "Unknown error",
            };
        }
    }

    /**
     * Met à jour la subscription Stripe (cancel/reactivate/change_plan)
     * @param companyId - ID de la company
     * @param action - Action à effectuer: 'cancel' | 'reactivate' | 'change_plan'
     * @param options - Options: cancel_at_period_end, new_plan, etc.
     * @param accessToken - Token JWT d'authentification
     * @returns Détails de la subscription mise à jour
     */
    static async updateSubscription(
        companyId: string,
        action: 'cancel' | 'reactivate' | 'change_plan',
        options: {
            cancel_at_period_end?: boolean;
            new_plan?: string;
        } = {},
        accessToken: string
    ) {
        try {
            const body = {
                action: action,
                ...options
            };

            const response = await FamlaServices(
                "api_host",
                `/stripe/subscription/${companyId}`,
                "PUT",
                body,
                accessToken
            );

            if (response.status === 200) {
                return {
                    success: true,
                    data: response.body.data,
                };
            } else if (response.status === 401) {
                return {
                    success: false,
                    error: "Unauthorized",
                    message: response.body.description || "Authentication required",
                };
            } else if (response.status === 404) {
                return {
                    success: false,
                    error: "Not Found",
                    message: response.body.description || "No active subscription found",
                };
            } else if (response.status === 400) {
                return {
                    success: false,
                    error: "Bad Request",
                    message: response.body.description || "Invalid subscription update request",
                };
            } else {
                return {
                    success: false,
                    error: "API Error",
                    message: response.body.description || "Failed to update subscription",
                };
            }
        } catch (error) {
            console.error("[StripeService] updateSubscription error:", error);
            return {
                success: false,
                error: "Network Error",
                message: error instanceof Error ? error.message : "Unknown error",
            };
        }
    }

    /**
     * Formate le message d'erreur pour affichage utilisateur
     * @param error - Erreur retournée par l'API
     * @returns Message d'erreur formaté
     */
    static formatErrorMessage(error: any): string {
        if (typeof error === 'string') return error;
        if (error?.message) return error.message;
        if (error?.error === 'Network Error') {
            return 'Erreur de connexion. Vérifiez votre connexion internet et réessayez.';
        }
        if (error?.error === 'Unauthorized') {
            return 'Session expirée. Veuillez vous reconnecter.';
        }
        return 'Une erreur est survenue. Veuillez réessayer ou contacter le support.';
    }
}

export default StripeService;
